import React, { useEffect, useRef, useState } from 'react';
import { IconActivity, IconVolume, IconClockHour4, IconPlayerPlayFilled, IconPlayerPauseFilled, IconWaveSine, IconBroadcast } from '@tabler/icons-react';

const RadioPage = () => {
    const [currentTime, setCurrentTime] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(0.8);
    const audioRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        setCurrentTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        document.body.style.overflowX = 'hidden';

        return () => {
            document.body.style.overflowX = 'auto';
        };
    }, []);

    const togglePlay = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play().catch(error => {
                    console.error("Playback failed:", error);
                });
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (audioRef.current) {
            audioRef.current.volume = newVolume;
        }
    };

    return (
        <div className="w-full min-h-screen bg-[#f8fafc] flex flex-col items-center justify-start lg:justify-center p-4 pt-20 md:pt-32 pb-20 relative font-libre overflow-x-hidden">

            {/* === CINEMATIC LIGHTING SYSTEM (Light Mode with Azure Tints) === */}
            <div className="fixed inset-0 pointer-events-none select-none z-0">
                <div className="absolute top-[10%] left-[20%] w-[500px] h-[500px] bg-[#2a1273]/10 rounded-full blur-[120px] animate-pulse-slow"></div>
                <div className="absolute bottom-[0%] right-[10%] w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-[0.015] scale-[1.3]">
                    <img src="/trace png-01.png" alt="Branding" className="w-[80%] h-auto grayscale brightness-0" />
                </div>
            </div>

            {/* === MAIN CONTENT COMPOSITION === */}
            <div className="relative z-10 w-full max-w-7xl flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-24 px-6 md:px-12 animate-fade-in-scale">

                {/* Branding & Info Area */}
                <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left space-y-8 lg:max-w-xl order-1">
                    <div className="space-y-6">
                        <div className="relative inline-block">
                            <div className="bg-[#2a1273] p-6 md:p-10 rounded-[32px] md:rounded-[44px] shadow-[0_25px_80px_-15px_rgba(42,18,115,0.3)] border border-[#2a1273]/20">
                                <img
                                    src="/trace png-01.png"
                                    alt="Trace Radio Logo"
                                    className="h-16 md:h-28 lg:h-36 w-auto object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]"
                                />
                            </div>
                        </div>

                        <div className="px-2">
                            <h2 className="text-lg md:text-xl font-bold text-[#2a1273] uppercase tracking-[0.4em] mb-2 md:mb-3">Live Broadcast</h2>
                            <p className="text-[12px] md:text-[15px] text-gray-800 font-medium tracking-wide max-w-md leading-relaxed opacity-75 lg:px-0">
                                നന്മയും തിന്മയും പരസ്പരം തിരിച്ചറിയാനാകാത്ത വിധം ഇഴ ചേർന്ന് നിൽക്കുന്ന പുതിയ സോഷ്യൽ മീഡിയ കാലത്ത് ധാർമികതയും മൂല്യങ്ങളും ഉയർത്തിപിടിക്കുന്ന തിരുത്തൽ ശക്തികൾ ഉയർന്നു വരേണ്ടതുണ്ട്. ലോകം മുഴുവൻ അപശബ്ദങ്ങൾ കൊണ്ട് വീർപ്പുമുട്ടുമ്പോൾ ആത്മീയതയുടെ ഒരു ശബ്ദം, ഒരു സന്ദേശം അത് ശ്രോതാക്കളുടെ ഹൃദയങ്ങളിൽ ഉണ്ടാക്കുന്ന ശാന്തത അനിർവചനീയമാണ്. നമ്മുടെ ഓരോ സ്ക്രോളുകളും മനുഷ്യരുടെ ചിന്തകളിലൂടെയാണ് ഒഴുകുന്നത്. ആ ഒഴുക്കിനെ നന്മയുടെ ദിശയിലേക്ക് നയിക്കുക എന്ന ശ്രമകരമായ ദൗത്യമാണ് ട്രെയ്സ് റേഡിയോ ഏറ്റെടുക്കുന്നത്.
                            </p>
                        </div>
                    </div>

                    {/* Simple Stats HUD */}
                    <div className="flex items-center gap-8 pt-4">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-[#2a1273]/30 uppercase tracking-widest">Streaming</span>
                            <span className="text-[14px] font-bold text-[#2a1273]">HQ Audio</span>
                        </div>
                        <div className="w-[1px] h-8 bg-gray-200"></div>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-[#2a1273]/30 uppercase tracking-widest">Studio Status</span>
                            <span className="text-[14px] font-bold text-green-600">On Air</span>
                        </div>
                    </div>
                </div>

                {/* Device Area */}
                <div className="relative animate-slide-up order-2 transform scale-[0.9] xs:scale-[0.95] sm:scale-100 origin-center transition-transform duration-700">

                    {/* Device Glow */}
                    <div className="absolute -inset-16 bg-gradient-to-tr from-[#2a1273]/5 via-transparent to-pumpkinToast-500/5 rounded-full blur-[80px] opacity-40"></div>

                    {/* Clean CSS Phone Case */}
                    <div className="relative w-[280px] h-[550px] md:w-[300px] md:h-[600px] bg-[#0c0c0c] rounded-[50px] p-[8px] shadow-[0_45px_100px_-25px_rgba(0,0,0,0.15)] border-[2.5px] border-[#222]">

                        {/* Internal Screen - LUMINOUS ROYAL BLUE */}
                        <div className="relative w-full h-full bg-gradient-to-br from-[#2a1273] to-[#1e1b4b] rounded-[42px] overflow-hidden flex flex-col shadow-inner">
                            {/* Animated Azure Aura */}
                            <div className="absolute inset-0 z-0">
                                <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-600 rounded-full blur-[80px] opacity-40"></div>
                                <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-indigo-500 rounded-full blur-[90px] opacity-30"></div>
                            </div>

                            {/* StatusBar */}
                            <div className="w-full h-8 px-6 flex items-center justify-between z-40 relative">
                                <span className="text-[8px] font-bold text-white/50">{currentTime}</span>
                                <div className="flex gap-1.5 items-center">
                                    <div className="w-2.5 h-1.5 border border-white/30 rounded-sm"></div>
                                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                                </div>
                            </div>

                            {/* Clean Minimalist Content Wrapper */}
                            <div className="flex-1 flex flex-col items-center justify-around py-12 px-6 text-center z-10 relative">

                                {/* Core Branding Focus - Larger & Sharper */}
                                <div className="space-y-6">
                                    <div className="w-32 h-32 mx-auto bg-gradient-to-br from-[#2a1273] to-[#0f0729] rounded-2xl flex items-center justify-center p-6 border border-white/10 shadow-2xl relative text-white">
                                        <img src="/trace png-01.png" alt="Logo" className="w-full h-auto drop-shadow-md" />
                                        {/* Status Glow */}
                                        <div className="absolute top-3 right-3 w-2 h-2 bg-green-500 rounded-full shadow-[0_0_8px_#22c55e]"></div>
                                    </div>
                                    <h4 className="text-[11px] font-black text-white/40 uppercase tracking-[0.4em]">Listen Live Radio</h4>
                                </div>

                                {/* The Core Player Terminal - Redesigned Custom UI (Compact Fit) */}
                                <div className='w-full space-y-6 px-2'>
                                    <audio 
                                        ref={audioRef}
                                        src="https://s4.radio.co/s97ba2fcc8/listen"
                                        onPlay={() => setIsPlaying(true)}
                                        onPause={() => setIsPlaying(false)}
                                    />

                                    {/* Visualizer / Waveform - Scaled Down */}
                                    <div className="flex items-end justify-center gap-1 h-10 px-4">
                                        {[...Array(15)].map((_, i) => (
                                            <div 
                                                key={i}
                                                className={`w-1 bg-gradient-to-t from-blue-400 to-indigo-300 rounded-full transition-all duration-300 ${isPlaying ? 'animate-equalizer' : 'h-1.5 opacity-30'}`}
                                                style={{ 
                                                    animationDelay: `${i * 0.1}s`,
                                                    height: isPlaying ? `${Math.floor(Math.random() * 60) + 40}%` : '6px'
                                                }}
                                            ></div>
                                        ))}
                                    </div>

                                    {/* Controls - Optimized Size */}
                                    <div className="flex flex-col items-center gap-6">
                                        <div className="relative">
                                            {/* Pulse Rings - Smaller */}
                                            {isPlaying && (
                                                <>
                                                    <div className="absolute inset-0 bg-white/20 rounded-full animate-ping scale-125 opacity-20"></div>
                                                    <div className="absolute inset-0 bg-white/10 rounded-full animate-pulse scale-[1.5] opacity-10"></div>
                                                </>
                                            )}
                                            
                                            <button 
                                                onClick={togglePlay}
                                                className="relative w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,255,255,0.4)] hover:scale-105 active:scale-95 transition-all duration-300 group z-10"
                                            >
                                                {isPlaying ? (
                                                    <IconPlayerPauseFilled className="w-8 h-8 text-[#2a1273]" />
                                                ) : (
                                                    <IconPlayerPlayFilled className="w-8 h-8 text-[#2a1273] ml-0.5" />
                                                )}
                                            </button>
                                        </div>

                                        {/* Volume & Info Box - Compact */}
                                        <div className="w-full space-y-3">
                                            <div className="flex items-center justify-between px-2">
                                                <div className="flex items-center gap-1.5">
                                                    <div className={`w-1.5 h-1.5 rounded-full ${isPlaying ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`}></div>
                                                    <span className="text-[9px] font-bold text-white/60 uppercase tracking-widest">
                                                        {isPlaying ? 'Live' : 'Paused'}
                                                    </span>
                                                </div>
                                                <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">128kbps</span>
                                            </div>

                                            <div className="flex items-center gap-2.5 bg-white/10 p-3 rounded-[20px] backdrop-blur-xl border border-white/10 shadow-xl">
                                                <IconVolume className="w-3.5 h-3.5 text-white/70" />
                                                <input 
                                                    type="range" 
                                                    min="0" 
                                                    max="1" 
                                                    step="0.01" 
                                                    value={volume}
                                                    onChange={handleVolumeChange}
                                                    className="flex-1 accent-white h-1 bg-white/20 rounded-full appearance-none cursor-pointer outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>



                            {/* Home Indicator */}
                            <div className="w-full h-6 flex items-center justify-center mb-1 z-40 relative">
                                <div className="w-16 h-1 bg-white/10 rounded-full"></div>
                            </div>

                            {/* Decorative Overlay */}
                            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-black/20 z-20"></div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx="true">{`
                .font-AndoraModernSerif { font-family: 'AndoraModernSerif', serif; }
                .mask-fade { mask-image: linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%); }
                
                @keyframes fade-in-scale { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
                @keyframes slide-up { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes equalizer { 0%, 100% { height: 20%; } 50% { height: 100%; } }
                @keyframes marquee-slow { 0% { transform: translateX(100%); } 100% { transform: translateX(-150%); } }
                @keyframes pulse-slow { 0% { transform: scale(1); opacity: 0.05; } 100% { transform: scale(1.1); opacity: 0.1; } }

                .animate-fade-in-scale { animation: fade-in-scale 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .animate-slide-up { animation: slide-up 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .animate-marquee-slow { animation: marquee-slow 15s linear infinite; }
                .animate-pulse-slow { animation: pulse-slow 10s infinite alternate ease-in-out; }
                .animate-equalizer { animation: equalizer 1.5s infinite ease-in-out; }
            `}</style>
        </div>
    );
};

export default RadioPage;
